import React from 'react';
import { Page, View, Document, StyleSheet, Text, Image, Font } from '@react-pdf/renderer';
import NotoSansJP from '../components/assets/fonts/NotoSansJP-Regular.otf';

Font.register({
  family: 'NotoSansJP',
  src: NotoSansJP,
});

const styles = StyleSheet.create({
  page: {
    fontFamily: 'NotoSansJP',
    fontSize: 8,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
  },
  sectionTitle: {
    fontSize: 12,
    marginBottom: 4,
    fontWeight: 'bold',
  },
  grid: {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    width: '100%',
  },
  column: {
    flex: 1,
    border: '1pt solid #bbb',
    padding: 6,
    borderRadius: 4,
    backgroundColor: '#ffffff',
  },
  categoryTitle: {
    fontSize: 10,
    marginTop: 4,
    marginBottom: 2,
    textDecoration: 'underline',
  },
  paymentTitle: {
    fontSize: 9,
    marginTop: 2,
    marginBottom: 1,
  },
  item: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderBottom: '0.5pt solid #ccc',
    paddingVertical: 2,
    gap: 4,
  },
  image: {
    width: 20,
    height: 20,
    objectFit: 'contain',
  },
  text: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexGrow: 1,
  },
  name: {
    fontSize: 5,
    maxWidth: '70%',
    wordBreak: 'break-word',
  },
  price: {
    fontSize: 7,
    textAlign: 'right',
    color: '#000',
  },
});

const renderItems = (items = []) =>
  items
    .filter((item) => item.product_name !== 'レジ袋')
    .map((item, idx) => (
      <View style={styles.item} key={idx}>
        <Image style={styles.image} src={`/media/product_images/${item.sku}.jpg?${new Date().getTime()}`} />
        <View style={styles.text}>
          <Text style={styles.name}>({item.inventory_id}) {item.product_name}</Text>
          <Text style={styles.price}>¥{item.unit_price}</Text>
        </View>
      </View>
    ));

const SellNotePdfDocument = ({ date, registerId, summaryData }) => {
  const summary = summaryData?.[registerId] || {};
  const period = summaryData?._period;
  const fmt = (value) => value ? String(value).replace('T', ' ').slice(0, 16) : '初回';

  return (
    <Document>
      <Page size="B5" style={styles.page}>
        <Text style={styles.sectionTitle}>レジID: {registerId} 販売ノート {date}</Text>
        {period && <Text style={{fontSize: 7, marginBottom: 4}}>
          集計：{fmt(period.start)} ～ {fmt(period.end)}
        </Text>}

        <View style={styles.grid}>
          {/* 通常販売 */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>通常販売</Text>
            {Object.entries(summary.normal || {}).map(([category, payments]) => {
              const allItems = Object.values(payments).flat();
              const nonBagItems = allItems.filter((item) => item.product_name !== 'レジ袋');
              if (category === '未分類' && nonBagItems.length === 0) return null;

              return (
                <View key={category}>
                  <Text style={styles.categoryTitle}>{category}</Text>
                  {Object.entries(payments).map(([method, items]) => (
                    <View key={method}>
                      <Text style={styles.paymentTitle}>支払方法: {method}</Text>
                      {renderItems(items)}
                    </View>
                  ))}
                </View>
              );
            })}
          </View>

          {/* 免税 1F */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>免税（1Fカテゴリ）</Text>
            {Object.entries(summary.tax_exempt || {}).map(([category, payments]) => {
              if (!category.includes('1F')) return null;
              return (
                <View key={category}>
                  <Text style={styles.categoryTitle}>{category}</Text>
                  {renderItems(Object.values(payments).flat())}
                </View>
              );
            })}
          </View>

          {/* 免税 2F */}
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>免税（2Fカテゴリ）</Text>
            {Object.entries(summary.tax_exempt || {}).map(([category, payments]) => {
              if (!category.includes('2F')) return null;
              return (
                <View key={category}>
                  <Text style={styles.categoryTitle}>{category}</Text>
                  {renderItems(Object.values(payments).flat())}
                </View>
              );
            })}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default SellNotePdfDocument;
